import React from "react";
import { Link } from "react-router-dom";

const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <section>
    <h2 className='text-2xl font-semibold mb-4'>{title}</h2>
    <div className='text-gray-300 space-y-3'>{children}</div>
  </section>
);

const MemsTermsOfService = () => {
  return (
    <div className='min-h-screen bg-black text-white p-8'>
      <div className='max-w-3xl mx-auto'>
        <h1 className='text-4xl font-bold mb-2'>Mems Terms of Service</h1>
        <p className='text-gray-500 mb-8'>Last updated October 4, 2026</p>

        <div className='space-y-8'>
          <p className='text-gray-300'>
            These Terms govern your use of the Mems iOS app, developed by Bobby Boy Holdings, Inc.
            (Anyone Labs) ("we," "us"). By downloading or using Mems, you agree to these Terms. If you
            don't agree, please don't use the app.
          </p>

          <Section title='1. Using the app'>
            <p>
              We give you a personal, non-exclusive, non-transferable license to use Mems on Apple devices
              you own or control, for personal, non-commercial use, subject to these Terms and the Apple
              Media Services Terms and Conditions. Mems is free to use.
            </p>
          </Section>

          <Section title='2. Accounts'>
            <p>
              You can use Mems without an account. To share memories with friends you'll sign in with your
              mobile number, verified by a one-time SMS code. You're responsible for activity on your account
              and for keeping access to your phone number. You can delete your account at any time in Mems
              Settings.
            </p>
          </Section>

          <Section title='3. Your content'>
            <p>
              Your photos, videos, and memories are yours — you keep all rights to them. When you share a
              memory, you grant us only the limited permission needed to sync and deliver that content to the
              people you chose. You're responsible for having the right to share what you share, including
              the consent of people who appear in it.
            </p>
          </Section>

          <Section title='4. People recognition'>
            <p>
              Mems can recognize the same person across your photos using on-device face matching. Matches
              are suggestions and may be wrong — always review who a memory will be shared with. Only tag and
              share people who would welcome it.
            </p>
          </Section>

          <Section title='5. Acceptable use'>
            <ul className='list-disc pl-6 space-y-2'>
              <li>
                Don't share content you don't have rights to, or that is unlawful, harmful, harassing, or
                violates someone's privacy.
              </li>
              <li>Don't send invitations to people who haven't asked for them in bulk or as spam.</li>
              <li>
                Don't reverse engineer, disrupt, or abuse the app, its sync infrastructure, or other users.
              </li>
            </ul>
          </Section>

          <Section title='6. Text message (SMS) program'>
            <p>
              <strong className='text-white'>Program name: Mems.</strong> Mems sends one-time sign-in codes
              to your number, and — when you invite a friend who isn't on Mems — a one-time invitation to the
              number you provide for them. These are transactional messages sent at your request; Mems does
              not send marketing texts. Message frequency varies. Message and data rates may apply. Reply STOP
              to opt out or HELP for help. Mobile numbers are not shared, sold, or rented to third parties or
              affiliates for marketing purposes. Carriers are not liable for delayed or undelivered messages.
            </p>
          </Section>

          <Section title='7. Availability'>
            <p>
              We work to keep Mems running, but the app is provided "as is" and "as available," without
              warranties of any kind. Features may change, and sharing depends on third-party services such as
              Apple iCloud that are outside our control.
            </p>
          </Section>

          <Section title='8. Limitation of liability'>
            <p>
              To the maximum extent permitted by law, we are not liable for any indirect, incidental, special,
              or consequential damages arising from your use of the app, including any loss of photos,
              memories, or data. Mems does not replace a backup — always keep your own backups of important
              photos.
            </p>
          </Section>

          <Section title='9. Termination'>
            <p>
              You may stop using Mems at any time by deleting the app and, if you have one, your account. We
              may suspend access if these Terms are violated.
            </p>
          </Section>

          <Section title='10. Changes'>
            <p>
              We may update these Terms. Material changes will be reflected by the "last updated" date above;
              continuing to use Mems after a change means you accept the updated Terms.
            </p>
          </Section>

          <Section title='11. Contact'>
            <p>
              Questions about these Terms:{" "}
              <a href='mailto:hello@sharemems.app' className='text-orange-400 hover:underline'>
                hello@sharemems.app
              </a>
            </p>
          </Section>
        </div>

        <div className='mt-12 flex gap-6 text-gray-400'>
          <Link to='/mems/privacy' className='hover:text-white'>Privacy Policy</Link>
          <Link to='/' className='hover:text-white'>Anyone Labs</Link>
        </div>
      </div>
    </div>
  );
};

export default MemsTermsOfService;
